import React from 'react';
import PropTypes from 'prop-types';
import Loading, { LoadingWrapper } from '../../LoadingComponent/LoadingComponent';
import ArrowKeyNavigation from '../../ArrowKeyNavigation/ArrowKeyNavigation';
import { isEmpty } from 'lodash';
import { NoMessageContainer, NoMessagesText } from './Queue.styles';
import EmptySearchState from './EmptySearchState';
import * as tabs from '../../../constants/tabNames';

import 'tippy.js/themes/light.css';
import TicketItem from './TicketItem/TicketItem';

class QueueComponent extends React.Component {
  containerRef = React.createRef();

  async componentDidMount() {
    await this.props.asyncHydrateExperts();
  }

  getSnapshotBeforeUpdate(prevProps) {
    // NOTE(vadim|SPD-14097|2026-02-12): When new tickets are added, preserve the visual position of the selected item
    if (
      this.containerRef.current &&
      prevProps.tickets.length < this.props.tickets.length &&
      this.props.selectedNonHistoricalTicketId
    ) {
      let scrollContainer = this.containerRef.current.parentElement;
      let iterationCount = 0;
      const maxIterations = 20; // Safety limit to prevent excessive DOM traversal

      while (scrollContainer && iterationCount < maxIterations) {
        const overflowY = window.getComputedStyle(scrollContainer).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
          break;
        }
        scrollContainer = scrollContainer.parentElement;
        iterationCount++;
      }

      if (scrollContainer) {
        const selectedElement = this.containerRef.current.querySelector('[tabindex="0"]');

        if (selectedElement) {
          return {
            scrollContainer,
            scrollTop: scrollContainer.scrollTop,
            selectedTicketOffsetTop: selectedElement.offsetTop,
            selectedTicketId: this.props.selectedNonHistoricalTicketId,
          };
        }
      }
    }

    return null;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    // NOTE(vadim|SPD-14097|2026-02-12): Adjust scroll so selected item stays at the same visual position
    if (snapshot && snapshot.selectedTicketId && this.containerRef.current) {
      const newIndex = this.props.tickets.findIndex((t) => t.ticketId === snapshot.selectedTicketId);

      if (newIndex !== -1) {
        const selectedElement = this.containerRef.current.querySelector(`[data-focus-index="${newIndex}"]`);

        if (selectedElement) {
          const offsetChange = selectedElement.offsetTop - snapshot.selectedTicketOffsetTop;
          snapshot.scrollContainer.scrollTop = snapshot.scrollTop + offsetChange;
        }
      }
    }
  }

  isLoadingActive() {
    return this.props.loading && this.props.loading.isLoading && this.props.loading.component === 'inbox';
  }

  renderNoTicketsView = () => {
    switch (this.props.inboxTab) {
      case tabs.NOMINATED:
        return renderMessageContainer(this.props.isAdmin ? 'NO ESCALATED TICKETS' : 'NO INCOMING MESSAGES');
      case tabs.ASSIGNED:
      case tabs.LIVE_CHAT:
        return renderMessageContainer('NO ITEMS HAVE BEEN ASSIGNED YET');
      case tabs.ALL:
        // NOTE(vlad|SPD-14096|2026-02-12): Show empty search state if no search has been performed yet
        if (!this.props.hasSearchedAllTab) {
          return <EmptySearchState />;
        }

        // NOTE(vlad|SPD-14096|2026-02-12): Show "no results" message if search was performed but returned nothing
        return renderMessageContainer('NO TICKETS FOUND');
      default:
        return null;
    }

    function renderMessageContainer(text) {
      return (
        <NoMessageContainer>
          <NoMessagesText>{text}</NoMessagesText>
        </NoMessageContainer>
      );
    }
  };

  renderLoadingSpinner = () => (
    <LoadingWrapper>
      <Loading delay={0} />
    </LoadingWrapper>
  );

  render() {
    const { tickets } = this.props;

    if (this.isLoadingActive()) {
      return this.renderLoadingSpinner();
    }

    // NOTE(vlad|SPD-14305|2026-02-27): For ALL tab, only show tickets if user
    // has explicitly clicked Search. This must run before the tickets check so
    // that stale allTickets from initial loadTicketsAdmin don't leak through.
    if (this.props.inboxTab === tabs.ALL && !this.props.hasSearchedAllTab) {
      return <EmptySearchState />;
    }

    if (isEmpty(tickets)) {
      return this.renderNoTicketsView();
    }

    return (
      <div ref={this.containerRef}>
        <ArrowKeyNavigation name="QueueComponent" mode={ArrowKeyNavigation.mode.VERTICAL}>
          {tickets.map((ticket) => (
            <TicketItem key={ticket.ticketId} ticket={ticket} {...this.props} />
          ))}
        </ArrowKeyNavigation>
      </div>
    );
  }
}

QueueComponent.propTypes = {
  tickets: PropTypes.arrayOf(
    PropTypes.shape({
      fromChannel: PropTypes.string.isRequired,
      smbName: PropTypes.string.isRequired,
      ticketBody: PropTypes.string,
      conversationUpdatedAt: PropTypes.string.isRequired,
    }).isRequired
  ).isRequired,
  inboxTab: PropTypes.string.isRequired,
  onShowMessageDetails: PropTypes.func.isRequired,
  hasSearchedAllTab: PropTypes.bool,
  selectedNonHistoricalTicketId: PropTypes.string,
};

export default QueueComponent;
